import React, { useEffect, useRef, useState } from 'react';
import Input from '../../../components/forms/Input';
import FilterPopup from '../../../components/popup/filterPopup/FilterPopup';
import SearchResultItem from '../../../components/search/SearchResultItem';
import FadeTransition from '../../../components/animations/FadeTransition';
import useDebounce from '../../../hooks/useDebounce';
import useGetSearchResults from '../../mainFilter/filters/Destination/useGetSearchResults';
import { pushEvent } from '../../../utilities/analytics';
import { WHERE_FILTER_HOTEL_NAME_SEARCH, SEC_FILTER_OPEN, WHERE_FILTER_HOTEL_NO_MATCH } from '../../../constants/analyticEvents';
import {
  fetchStaticHotelById,
  setSelectedHotel,
  clearStaticDeal,
  fetchHotelById,
  setShowLoader,
  clearSearch,
} from '../../search/searchSlice';
import { setIsPricesFetched } from '../../locationDeals/locationDealsSlice';
import {setAppState, setLastChangedFilter } from '../../../appSlice';
import { setSingleDestination } from '../../mainFilter/mainFilterSlice';
import { useDispatch, useSelector } from 'react-redux';
import SearchResultLoader from '../../../components/loaders/SearchResultLoader';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import { isMobile } from '../../../utilities/helpers';
import SearchInput from '../../mainFilter/filters/Destination/SearchInput';
import { t } from '../../../utilities/translator';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import { setSecondaryFilterDefault, setSecondaryHotelFilterDefault } from '../secondaryFilterSlice';
import { selectActiveDestinations } from '../../mainFilter/selectors';

let noresTime; // timeout for no match event

const SearchByHotel = () => {
  const dispatch = useDispatch();
  const ref = useRef();
  const { selectedHotel, ...search } = useSelector(state => state.userSearch);
  const { guestFilterTooltip } = useSelector(state => state.app);
  const activeDestinations = useSelector(selectActiveDestinations);
  const [value, setValue] = useState('');
  const [label, setLabel] = useState(selectedHotel.hotelName);
  const [isOpen, setIsOpen] = useState(false);
  const debounceValue = useDebounce(value, 500);
  const clean = !selectedHotel.hotelId;

  useOnClickOutside(ref, () => isOpen && setIsOpen(false));
  useBodyScrollLock({ condition: isOpen, mobileOnly: true });
  const { hotels } = useGetSearchResults(debounceValue);

  useEffect(() => {
    setLabel(selectedHotel.hotelName);
  }, [selectedHotel.hotelName])

  useEffect(() => {
    if (debounceValue) {
      setIsOpen(true);
      pushEvent(SEC_FILTER_OPEN, { filterName: 'hotel_name', value: debounceValue });
    }
    clearTimeout(noresTime);
    if (search.state === 'loaded' && hotels.length === 0 && debounceValue) {
      const _searchValue = debounceValue;
      noresTime = setTimeout(() => {
        pushEvent(WHERE_FILTER_HOTEL_NO_MATCH, { searchPhrase: _searchValue });
      }, 2000);
    }
  }, [debounceValue]);

  useEffect(() => {
    if (!activeDestinations || activeDestinations.length !== 1) {
      setLabel('');
      setValue('');
    }
  }, [activeDestinations])

  useEffect(() => {
    if (guestFilterTooltip && isOpen) {
      dispatch(setAppState({ guestFilterTooltip: false }))
    }
  }, [isOpen, guestFilterTooltip, dispatch])

  const onHotelClick = (hotel) => {
    dispatch(setSelectedHotel(hotel));
    dispatch(setIsPricesFetched(false));
    dispatch(setLastChangedFilter('where'));
    // dispatch(setSecondaryFilterDefault());
    dispatch(setSecondaryHotelFilterDefault()); // reset only hotel filters
    setIsOpen(false);
    setLabel(hotel.hotelName);

    if (+search.staticDeal?.id !== hotel.hotelId) {
      dispatch(clearStaticDeal());
    }

    if (!hotel.hfDestinationId && hotel.hotelId) {
      dispatch(fetchHotelById(hotel.hotelId));
    }

    dispatch(fetchStaticHotelById({
      id: hotel.hotelId,
      destination: hotel.hfDestinationId
    }));
    dispatch(setShowLoader(true));

    if (
      activeDestinations.length !== 1 ||
      (activeDestinations.length === 1 && activeDestinations[0].id !== hotel.hfDestinationId)
    ) {
      dispatch(setSingleDestination(hotel.hfDestinationId))
    }

    pushEvent(WHERE_FILTER_HOTEL_NAME_SEARCH, { hotelName: hotel.hotelName, hotel });
  }

  const wrapSearch = (string_) => {
    let result = string_;

    value.split(/[ ,]/gi).forEach((value) => {
      const regex = new RegExp(value, "gi");

      if (regex.test(string_) && value.length > 1) {
        const searchPosition = result.toLowerCase().indexOf(value.toLowerCase());

        result = `${result.slice(0, searchPosition)}<b>${result.slice(
          searchPosition,
          searchPosition + value.length
        )}</b>${result.slice(searchPosition + value.length)}`
      }
    });

    return result;
  };

  const onSearchChange = (value) => {
    setValue(value)
    setLabel(value)
  }

  const onInputClick = () => {
    if (isMobile()) {
      setIsOpen(true);
    } else if (selectedHotel.hotelName || value) {
      setIsOpen(true);
    }
  }

  const onClearSearch = () => {
    dispatch(clearSearch());
    setValue('');
    setLabel('');
  }

  return (
    <div className="search-by-hotel" ref={ref}>
      <div className="search-by-hotel__input-wrap" title={t('searchByHotel.header')}>
        <i className='ri-search-line search-by-hotel__search' />
        <Input
          type="search"
          placeholder={t('searchByHotel.header')}
          value={label || ''}
          onChange={onSearchChange}
          onClick={onInputClick}
          className={clean ? '' : 'hotel-selected'}
        />
        {!clean && <i className='ri-close-line search-by-hotel__clear' onClick={onClearSearch} />}
      </div>

      <FadeTransition
        in={isOpen}
        unmountOnExit
      >
        <FilterPopup className="search-by-hotel-popup">
          <FilterPopup.Header>
            {t('searchByHotel.header')}
          </FilterPopup.Header>

          <FilterPopup.Body>
            {(debounceValue || selectedHotel.hotelName) && (
              <div
                className='search-label desktopOnly'
                dangerouslySetInnerHTML={{
                  __html: t('searchByHotel.label', { value: debounceValue || selectedHotel.hotelName }),
                }}
              />
            )}

            <div className="mobileOnly">
              <SearchInput
                placeholder="holidayFinder.destinations.searchByHotelPlaceholder"
                onChange={(e) => onSearchChange(e.target.value)}
                value={label}
                autoFocus
              />
            </div>

            <div className="search-list" dir="ltr">
              {search.state === 'loading' ? (
                <div className="search-list">
                  <SearchResultLoader />
                  <SearchResultLoader />
                  <SearchResultLoader />
                  <SearchResultLoader />
                </div>
              ) : (
                <>
                  {hotels.map(hotel => (
                    <SearchResultItem
                      key={hotel.hotelId}
                      title={wrapSearch(hotel.hotelName)}
                      subtitle={wrapSearch(hotel.hfDestinationName)}
                      type="hotel"
                      onClick={() => onHotelClick(hotel)}
                    />
                  ))}
                </>
              )}

              {search.state === 'loaded' && hotels.length === 0 && (
                <div className="search-list__no-matches">{t('destinations.searchNoResults')}</div>
              )}
            </div>

          </FilterPopup.Body>

          <FilterPopup.Footer>
            <div className="btn-group">
              <button
                className="icon-close filter-popup__close"
                onClick={() => setIsOpen(false)}
              />
            </div>
          </FilterPopup.Footer>
        </FilterPopup>
      </FadeTransition>
    </div>
  )
}

export default React.memo(SearchByHotel);
